import { Vector2, Vector3, Plane, BoxGeometry, MeshBasicMaterial, Mesh } from 'three';
import { CSS3DObject } from 'three/examples/jsm/renderers/CSS3DRenderer.js';
import { ReactiveComponent } from '../../core/ReactiveComponent.core.js';
import { computeProjectiveMatrix3d } from '../../plugins/3d-2d-transform/src/projectiveMatrix.js';
import { vapiPerspectiveCornerHandleStyle } from '../../constants/vapiPerspectiveHandles.js';
import { IframeElement } from '../iframe-UI/IframeElement.js';
import { ComponentRegistry } from '../../constants/componentRegistry.js';

function parsePixelFromDim(value) {
    if (value == null || value === '') return 0;
    const m = String(value).trim().match(/^(\d+(?:\.\d+)?)px$/i);
    return m ? Number(m[1]) : 0;
}

/**
 * VRIframeElement - CSS3D iframe embed with perspective warping (like VRVapiChairWithCSS3D).
 */
class VRIframeElement extends ReactiveComponent {
    constructor(params = {}) {
        super(null, {
            mode: params.editMode ? 'edit' : 'view'
        });

        const { model, camera, controls, raycaster, position, id, dbConfig, sizes } = params;
        this.id = id;
        this.model = model;
        this.camera = camera;
        this.controls = controls || null;
        this.position = position || { x: 0, y: 0, z: -540 };
        this.dbConfig = dbConfig || {};
        this._raycaster = raycaster || null;
        this._sizes = sizes || { width: window.innerWidth, height: window.innerHeight };
        this._viewportEl = params.viewportElement || document.querySelector('#webgl') || document.querySelector('#css3d');
        this._ndc = new Vector2();
        this._dragPlane = new Plane();
        this._dragPlaneNormal = new Vector3();
        this._planeIntersectPoint = new Vector3();
        this._dragGrabOffset = new Vector3();
        this._tmpVec = new Vector3();
        this._dragStartDistance = 0;
        this.isDragging = false;
        this._captureEl = null;
        this._isEditMode = !!params.editMode;
        this._css3dScale = 1;
        this._userScale = Number(this.dbConfig?.size ?? this.dbConfig?.scale) || 1;
        this._cornerHandles = [];
        this.parent = this;
        this.name = 'iframeElement';
        this.type = 'iframeElement';
        this.objConfig = {
            isResizable: true,
            isRotatable: true,
        };

        this._buildNode();
        this.anchor.position.set(
            this.position.x,
            this.position.y,
            this.position.z !== undefined ? this.position.z : -540
        );
        this.position = this.anchor.position;
        this.anchor.userData.vrIframeElement = this;

        if (this.state.mode === 'edit') {
            this._attachDragListeners();
        }
        this._applyVisibilityFromConfig();
        return this;
    }

    _resolveRootDimensions() {
        const dims = IframeElement.resolveDimensions(this.dbConfig?.iframe_value, this.dbConfig);
        let domW = Number(this.dbConfig?.width) > 0 ? Number(this.dbConfig.width) : dims.width;
        let domH = Number(this.dbConfig?.height) > 0 ? Number(this.dbConfig.height) : dims.height;
        let widthStyle = `${domW}px`;
        let heightStyle = `${domH}px`;

        if (this.dbConfig?.iframe_width_enabled && this.dbConfig?.iframe_width) {
            widthStyle = String(this.dbConfig.iframe_width);
            const pxW = parsePixelFromDim(widthStyle);
            if (pxW > 0) domW = pxW;
        }
        if (this.dbConfig?.iframe_height_enabled && this.dbConfig?.iframe_height) {
            heightStyle = String(this.dbConfig.iframe_height);
            const pxH = parsePixelFromDim(heightStyle);
            if (pxH > 0) domH = pxH;
        }

        return { widthStyle, heightStyle, domW, domH };
    }

    _applyRootDimensions() {
        if (!this.root) return;
        const prevW = this._domW || this.root.offsetWidth || 1;
        const prevH = this._domH || this.root.offsetHeight || 1;
        const { widthStyle, heightStyle, domW, domH } = this._resolveRootDimensions();
        this.root.style.width = widthStyle;
        this.root.style.height = heightStyle;
        // Force layout so CSS3D preview picks up the new box size immediately.
        const ew = this.root.offsetWidth || domW;
        const eh = this.root.offsetHeight || domH;
        if (this._perspectiveCorners?.length === 4 && (prevW !== ew || prevH !== eh)) {
            const scaleX = ew / prevW;
            const scaleY = eh / prevH;
            if (Number.isFinite(scaleX) && Number.isFinite(scaleY) && (scaleX !== 1 || scaleY !== 1)) {
                for (let i = 0; i < 4; i++) {
                    this._perspectiveCorners[i] = {
                        x: this._perspectiveCorners[i].x * scaleX,
                        y: this._perspectiveCorners[i].y * scaleY,
                    };
                }
                this.dbConfig = {
                    ...this.dbConfig,
                    perspectiveOffsets: this._perspectiveOffsetsForDb(),
                };
            }
        }
        this._domW = ew;
        this._domH = eh;
        this._originalPos = [{ x: 0, y: 0 }, { x: ew, y: 0 }, { x: ew, y: eh }, { x: 0, y: eh }];
        this._syncHandlePositions?.();
        this._applyPerspectiveTransform?.();
    }

    _buildNode() {
        const { widthStyle, heightStyle, domW, domH } = this._resolveRootDimensions();
        const w = this._domW = domW;
        const h = this._domH = domH;
        this._originalPos = [{ x: 0, y: 0 }, { x: w, y: 0 }, { x: w, y: h }, { x: 0, y: h }];

        this._iframeInstance = new IframeElement({
            width: w,
            height: h,
            dbConfig: this.dbConfig,
            iframeValue: this.dbConfig?.iframe_value,
            interactive: !this._isEditMode,
        });
        const iframeContent = this._iframeInstance.render();
        iframeContent.style.width = '100%';
        iframeContent.style.height = '100%';
        if (this._isEditMode) {
            iframeContent.style.pointerEvents = 'none';
        }

        const dataId = `iframe_element_${this.id}`;
        Array.from(document.querySelectorAll(`[data-id='${dataId}']`)).forEach((node) => node?.remove?.());

        const root = document.createElement('div');
        root.setAttribute('class', 'vrai-agent-component video_element vr-iframe-element-wrapper css3d_test_wrapper');
        root.setAttribute('data-css3d-interactive', 'true');
        root.setAttribute(
            'data-css3d-click-target',
            '.vr-iframe-element-perspective-content, .vr-iframe-element-corner-handle'
        );
        root.setAttribute('data-id', dataId);
        root.style.cssText = `
            position: relative;
            width: ${widthStyle};
            height: ${heightStyle};
            pointer-events: ${this._isEditMode ? 'auto' : 'auto'};
            will-change: transform;
            user-select: none;
            -webkit-user-select: none;
        `;
        root.classList.add(`vr-iframe-element-${this.id}`);

        if (this._isEditMode) {
            root.addEventListener('click', (e) => e.stopPropagation());
        }

        root.addEventListener('pointerdown', (e) => {
            if (e.button !== 0) return;
            if (e.target?.closest?.('.vr-iframe-element-corner-handle')) return;
            const hitPerspective = e.target?.closest?.('.vr-iframe-element-perspective-content')
                ?? document.elementFromPoint(e.clientX, e.clientY)?.closest?.('.vr-iframe-element-perspective-content');
            if (this.state.mode === 'edit' && hitPerspective && this._onPointerDown) {
                e.preventDefault();
                e.stopPropagation();
                this._onPointerDown(e);
            }
        });

        const saved = this.dbConfig?.perspectiveOffsets;
        this._perspectiveCorners = Array.isArray(saved) && saved.length === 4
            ? saved.map((p) => (p.xr !== undefined
                ? { x: Number(p.xr) * w, y: Number(p.yr) * h }
                : { x: Number(p.x) ?? p.x, y: Number(p.y) ?? p.y }))
            : this._originalPos.map((p) => ({ ...p }));

        const perspectiveContent = document.createElement('div');
        perspectiveContent.className = 'vr-iframe-element-perspective-content';
        perspectiveContent.style.cssText = `position:absolute;inset:0;overflow:hidden;transform-origin:0 0;transform-style:flat;will-change:transform;pointer-events:${this._isEditMode ? 'auto' : 'auto'};`;
        if (this._isEditMode) perspectiveContent.style.cursor = 'grab';
        perspectiveContent.appendChild(iframeContent);
        this._perspectiveContent = perspectiveContent;

        const perspectiveWrapper = document.createElement('div');
        perspectiveWrapper.className = 'vr-iframe-element-perspective-wrapper';
        perspectiveWrapper.style.cssText = 'position:absolute;inset:0;overflow:visible;pointer-events:none;';
        this._perspectiveWrapper = perspectiveWrapper;
        perspectiveWrapper.appendChild(perspectiveContent);

        this._applyPerspectiveTransform = () => {
            const ew = root.offsetWidth || this._domW;
            const eh = root.offsetHeight || this._domH;
            const currentOrigPos = [{ x: 0, y: 0 }, { x: ew, y: 0 }, { x: ew, y: eh }, { x: 0, y: eh }];
            const m = computeProjectiveMatrix3d(currentOrigPos, this._perspectiveCorners);
            perspectiveContent.style.transform = m;
            this._syncHandlePositions?.();
        };
        this._applyPerspectiveTransform();

        if (this.state.mode === 'edit') {
            for (let i = 0; i < 4; i++) {
                const hnd = document.createElement('div');
                hnd.className = 'vr-iframe-element-corner-handle';
                hnd.dataset.corner = String(i);
                hnd.style.cssText = vapiPerspectiveCornerHandleStyle('pointer-events:auto;');
                const updateHandlePos = () => {
                    hnd.style.left = `${this._perspectiveCorners[i].x}px`;
                    hnd.style.top = `${this._perspectiveCorners[i].y}px`;
                };
                updateHandlePos();
                const onCornerPointerDown = (e) => {
                    if (this.dbConfig?.lockedInEdit) return;
                    e.preventDefault();
                    e.stopPropagation();
                    hnd.style.cursor = 'grabbing';
                    const startX = e.clientX;
                    const startY = e.clientY;
                    const startPos = { x: this._perspectiveCorners[i].x, y: this._perspectiveCorners[i].y };
                    const onMove = (ev) => {
                        const dx = ev.clientX - startX;
                        const dy = ev.clientY - startY;
                        this._perspectiveCorners[i].x = startPos.x + dx;
                        this._perspectiveCorners[i].y = startPos.y + dy;
                        updateHandlePos();
                        this._applyPerspectiveTransform();
                    };
                    const onUp = () => {
                        hnd.style.cursor = 'grab';
                        document.removeEventListener('pointermove', onMove);
                        document.removeEventListener('pointerup', onUp);
                        this.dbConfig = {
                            ...this.dbConfig,
                            perspectiveOffsets: this._perspectiveOffsetsForDb(),
                        };
                        if (this.model?.view?.updateJSON) this.model.view.updateJSON(this.anchor, null);
                    };
                    document.addEventListener('pointermove', onMove);
                    document.addEventListener('pointerup', onUp);
                };
                hnd.addEventListener('pointerdown', onCornerPointerDown);
                perspectiveWrapper.appendChild(hnd);
                this._cornerHandles.push(hnd);
            }
        }

        this._syncHandlePositions = () => {
            this._cornerHandles.forEach((hnd, i) => {
                if (this._perspectiveCorners[i]) {
                    hnd.style.left = `${this._perspectiveCorners[i].x}px`;
                    hnd.style.top = `${this._perspectiveCorners[i].y}px`;
                }
            });
        };

        root.appendChild(perspectiveWrapper);

        this.root = root;
        this.dom = root;
        this.css3dObject = new CSS3DObject(root);
        const initialScale = (this._css3dScale = this._getViewportScale()) * (this._userScale ?? 1);
        this.css3dObject.scale.set(initialScale, initialScale, 1);
        this._syncCornerHandleInverseScale();

        const geo = new BoxGeometry(0.1, 0.1, 0.1);
        const mat = new MeshBasicMaterial({ visible: false });
        this.anchor = new Mesh(geo, mat);
        this.anchor.name = 'iframeElement';
        this.anchor.type = 'iframeElement';
        this.anchor.groupDetails = {
            title: (typeof $trans !== 'undefined' && $trans('iframe-text')) || 'iFrame',
            icon: 'web',
            editTitle: (typeof $trans !== 'undefined' && $trans('edit-iframe-agent')) || 'Edit iframe',
            dataLang: 'edit-iframe-agent',
        };
        this.anchor.add(this.css3dObject);

        this.elementClass = `vr-iframe-element-${this.id}`;
        this.mesh = this.anchor;
        this.object = this.anchor;
    }

    _perspectiveOffsetsForDb() {
        const ew = this.root?.offsetWidth || this._domW || 1;
        const eh = this.root?.offsetHeight || this._domH || 1;
        return (this._perspectiveCorners || []).map((p) => ({
            x: p.x,
            y: p.y,
            xr: p.x / ew,
            yr: p.y / eh,
        }));
    }

    _getViewportScale() {
        const vpEl = this._viewportEl;
        const canvasH = vpEl?.clientHeight || 0;
        const winH = window.innerHeight;
        return (canvasH > 0 && winH > canvasH) ? winH / canvasH : 1;
    }

    _syncCornerHandleInverseScale() {
        if (!this.css3dObject) return;
        const s = Math.max(this.css3dObject.scale.x || 1, 0.02);
        const inv = 1 / s;
        const t = `translate(-50%,-50%) scale(${inv})`;
        for (const hnd of this._cornerHandles) {
            hnd.style.transform = t;
        }
    }

    _applyVisibilityFromConfig() {
        const inEditMode = !!this.model?.editMode || this.state.mode === 'edit';
        const hiddenByHideOnStart = !inEditMode && !!this.dbConfig?.hideOnStart;
        this.anchor.visible = !hiddenByHideOnStart;
        this._syncDomVisibilityToAnchor();
    }

    _syncDomVisibilityToAnchor() {
        if (!this.dom?.style) return;
        const visible = this.anchor?.visible !== false;
        this.dom.classList.toggle('visible', visible);
        this.dom.classList.toggle('hide-on-start', !visible);
        this.dom.style.display = visible ? '' : 'none';
    }

    _clientToNDC(clientX, clientY) {
        const el = this._viewportEl;
        if (!el) {
            this._ndc.x = (clientX / this._sizes.width) * 2 - 1;
            this._ndc.y = -(clientY / this._sizes.height) * 2 + 1;
            return;
        }
        const rect = el.getBoundingClientRect();
        this._ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
        this._ndc.y = 1 - ((clientY - rect.top) / rect.height) * 2;
    }

    _setFromClientXY(clientX, clientY) {
        this._clientToNDC(clientX, clientY);
        this._raycaster.setFromCamera(this._ndc, this.camera);
        if (this._raycaster.ray.intersectPlane(this._dragPlane, this._planeIntersectPoint)) {
            this._tmpVec.copy(this._planeIntersectPoint).add(this._dragGrabOffset);
            this._tmpVec.sub(this.camera.position).normalize();
            this._tmpVec.multiplyScalar(this._dragStartDistance).add(this.camera.position);
            if (this.anchor.parent) {
                this.anchor.parent.updateMatrixWorld(true);
                this.anchor.parent.worldToLocal(this._tmpVec);
                this.anchor.position.copy(this._tmpVec);
            } else {
                this.anchor.position.copy(this._tmpVec);
            }
        }
    }

    _onPointerMove = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!this.isDragging) return;
        this._setFromClientXY(e.clientX, e.clientY);
        this.anchor.lookAt(this.camera.position);
    };

    _onPointerUp = (e) => {
        if (!this.isDragging) return;
        this.isDragging = false;
        if (this._perspectiveContent) this._perspectiveContent.style.cursor = 'grab';
        if (this._captureEl) {
            try { this._captureEl.releasePointerCapture(e.pointerId); } catch (err) { }
            this._captureEl = null;
        }
        window.removeEventListener('pointermove', this._onPointerMove);
        window.removeEventListener('pointerup', this._onPointerUp);
        window.removeEventListener('pointercancel', this._onPointerUp);
        if (this.controls) this.controls.enabled = true;
        if (this.model?.view) {
            if (this.model.view.updateJSON) this.model.view.updateJSON(this.anchor, null);
            this.model.view.elementSelected = this;
            this.showObjectMenu();
            if (typeof $ !== 'undefined') $('.edit_element').trigger('click');
        }
    };

    _onPointerDown = (e) => {
        if (this.state.mode !== 'edit') return;
        if (this.dbConfig?.lockedInEdit) return;
        if (this.isDragging) return;
        if (e.target?.closest?.('.vr-iframe-element-corner-handle')) return;
        this.isDragging = true;
        this._captureEl = e.currentTarget || this.dom;
        try { this._captureEl?.setPointerCapture?.(e.pointerId); } catch (err) { }
        if (this.anchor.parent) {
            this.anchor.parent.updateMatrixWorld(true);
            this.anchor.getWorldPosition(this._tmpVec);
        } else {
            this._tmpVec.copy(this.anchor.position);
        }
        this._dragPlaneNormal.subVectors(this._tmpVec, this.camera.position).normalize();
        this._dragPlane.setFromNormalAndCoplanarPoint(this._dragPlaneNormal, this._tmpVec);
        this._dragStartDistance = this.camera.position.distanceTo(this._tmpVec);
        this._clientToNDC(e.clientX, e.clientY);
        this._raycaster.setFromCamera(this._ndc, this.camera);
        if (this._raycaster.ray.intersectPlane(this._dragPlane, this._planeIntersectPoint)) {
            this._dragGrabOffset.copy(this._tmpVec).sub(this._planeIntersectPoint);
        } else {
            this._dragGrabOffset.set(0, 0, 0);
        }
        if (this._perspectiveContent) this._perspectiveContent.style.cursor = 'grabbing';
        window.addEventListener('pointermove', this._onPointerMove);
        window.addEventListener('pointerup', this._onPointerUp);
        window.addEventListener('pointercancel', this._onPointerUp);
        if (this.controls) this.controls.enabled = false;
    };

    _attachDragListeners() {
        if (this._perspectiveContent) this._perspectiveContent.style.cursor = 'grab';
    }

    _detachDragListeners() {
        if (this._perspectiveContent) this._perspectiveContent.style.cursor = '';
        window.removeEventListener('pointermove', this._onPointerMove);
        window.removeEventListener('pointerup', this._onPointerUp);
        window.removeEventListener('pointercancel', this._onPointerUp);
        this.isDragging = false;
        if (this.controls) this.controls.enabled = true;
    }

    render(camera) {
        if (!camera) return;
        if (!this.isDragging) {
            this.anchor.lookAt(camera.position);
            this._css3dScale = this._getViewportScale();
            const scale = this._css3dScale * (this._userScale ?? 1);
            if (this.css3dObject) this.css3dObject.scale.set(scale, scale, 1);
        }
        this._syncCornerHandleInverseScale();
    }

    getId() {
        return this.id;
    }

    setId(id) {
        this.id = id;
    }

    getDbConfig() {
        return this.dbConfig;
    }

    setDbConfig(config) {
        this.dbConfig = { ...this.dbConfig, ...config };
        if (config?.iframe_value !== undefined) {
            this._iframeInstance?.updateIframe(config.iframe_value);
        }
        if (config?.size !== undefined || config?.scale !== undefined) {
            const s = config.size ?? config.scale;
            this._userScale = Number(s) > 0 ? Number(s) : 1;
        }
        if (config?.perspectiveOffsets && Array.isArray(config.perspectiveOffsets) && this._perspectiveCorners) {
            const w = this.root?.offsetWidth || this._domW || 1;
            const h = this.root?.offsetHeight || this._domH || 1;
            this._perspectiveCorners.splice(0, 4, ...config.perspectiveOffsets.map((p) => (p.xr !== undefined && p.yr !== undefined)
                ? { x: Number(p.xr) * w, y: Number(p.yr) * h }
                : { x: Number(p.x) ?? p.x, y: Number(p.y) ?? p.y }));
            this._syncHandlePositions?.();
            this._applyPerspectiveTransform?.();
        }
        if (
            config?.iframe_width_enabled !== undefined
            || config?.iframe_width !== undefined
            || config?.iframe_height_enabled !== undefined
            || config?.iframe_height !== undefined
            || config?.width > 0
            || config?.height > 0
        ) {
            this._applyRootDimensions();
        }
        this._applyVisibilityFromConfig();
    }

    setIframeValue(value) {
        this.setDbConfig({ iframe_value: value || '' });
    }

    getLimits() {
        return { min: 280, max: 800, distance: Number(this.dbConfig?.distance) || 540 };
    }

    getAxisRotations() {
        return { rotateX: 0, rotateY: 0 };
    }

    setDistance(val) {
        const n = Number(val);
        if (!Number.isFinite(n) || n <= 0 || !this.anchor?.position?.setLength) return;
        const { min, max } = this.getLimits();
        const clamped = Math.min(max, Math.max(min, n));
        this.anchor.position.setLength(clamped);
        this.dbConfig = { ...this.dbConfig, distance: clamped };
    }

    setSize(val) {
        const n = Number(val);
        if (!Number.isFinite(n) || n <= 0) return;
        this._userScale = n;
        this.dbConfig = { ...this.dbConfig, size: n };
    }

    setAxisRotations() { }

    showObjectMenu() {
        const title = (typeof $trans !== 'undefined' && $trans('iframe-text')) || 'iFrame';
        $('.object-menu .object-name').text(title);
        $('.object-menu').addClass('is-active');
        $('.object-menu').addClass('edit');
        $('.iframe-agent-settings').removeClass('hidden').addClass('is-active');
        $('.vrai-agent-settings, .tooltip-settings, .video-loop, .video-transparent').addClass('hidden');
        $('.perspective-settings').removeClass('hidden');
    }

    toggleSelect() {
        this.dom?.querySelector('.vr-iframe-element-content')?.classList?.toggle('active');
    }

    resetPerspective() {
        if (!this._perspectiveCorners || !this._originalPos) return;
        this._perspectiveCorners.splice(0, 4, ...this._originalPos.map((p) => ({ ...p })));
        this._syncHandlePositions?.();
        this._applyPerspectiveTransform?.();
        this.dbConfig = { ...this.dbConfig, perspectiveOffsets: this._perspectiveOffsetsForDb() };
    }

    destroy() {
        try { this._detachDragListeners(); } catch (e) { }
        if (this.dom?.parentNode) this.dom.parentNode.removeChild(this.dom);
        document.querySelectorAll(`[data-id='iframe_element_${this.id}']`).forEach((node) => node?.remove?.());
    }
}

ComponentRegistry.register({
    type: 'iframeElement',
    displayNameKey: 'iframe-text',
    getDisplayName: () => (typeof $trans !== 'undefined' && $trans('iframe-text')) || 'iFrame',
    events: [
        { value: 'click', labelKey: 'event-click' },
        { value: 'raycastEnter', labelKey: 'event-raycast-enter' },
        { value: 'raycastLeave', labelKey: 'event-raycast-leave' },
    ],
    actions: [],
});

export { VRIframeElement };
